import { useEffect, useMemo, useRef, useState } from 'react'
import firebase from '../firebase/compat'
import { db } from '../firebase/config'
import checkAddress from '../helpers/pdok/checkAddress'
import { geocodeInputKey, canReuseLocation, buildLocationWrite, adressLocationInput } from '../helpers/pdok/planAdressLocation'
import { client } from './Client'

// Welke huishoudens hebben deze pagina-sessie al een deelname-aanroep gehad?
// De functie zelf is idempotent, maar een rondgang per paginabezoek is zonde.
const gemeldeHuishoudens = new Set()

/**
 * Meld dit huishouden aan bij de programma's van zijn organisatie. Idempotent,
 * en een mislukking mag het scherm niet raken — het adres is al opgeslagen.
 * AI-CONTEXT: See docs/features/energiearmoede-arnhem-ketensamenwerking.md
 * AI-CONTEXT: See docs/pitfalls.md#api-error-claims-nothing-was-saved
 *
 * @param {string} adressID - `Adresses.ID`, niet de docid
 * @returns {Promise<void>}
 */
const meldDeelnameAan = async (adressID) => {
    if (!adressID || !client) return

    const sleutel = `${client}:${adressID}`
    if (gemeldeHuishoudens.has(sleutel)) return
    gemeldeHuishoudens.add(sleutel)

    try {
        // Regio expliciet: de standaard-client wijst naar us-central1 en zou de
        // functie niet vinden.
        // AI-CONTEXT: See docs/pitfalls.md#api-firebase-v1-region-default
        await firebase.app().functions('europe-west1')
            .httpsCallable('ensureHouseholdParticipation')({ adressID, client })
    } catch (error) {
        // Opnieuw proberen bij een volgend bezoek mag wél.
        gemeldeHuishoudens.delete(sleutel)
        console.error('Deelname-rij aanmelden mislukt', { adressID }, error)
    }
}

// Lazy geocoder: reads `Adresses.Location` first, only calls PDOK when the
// stored Location didn't come from the current postcode + huisnummer +
// toevoeging, then persists the result back to the Adresses doc so the next
// page load is instant. Aligns with `docs/features/external-context-layer.md`
// Phase 1. Which Location may be reused and what gets written lives in
// planAdressLocation (tested); the PDOK lookup in checkAddress (tested).
// AI-CONTEXT: See docs/features/postcode-huisnummer-controle.md
const useEnsureAdressLocation = (adress, personaData) => {
    const existing = adress?.Location || null
    const adressDocid = adress?.docid || ''
    // `ID` en niet `docid`: de deelname-rij verwijst naar Adresses.ID, want dat
    // is het veld waarop de rest van de app een huishouden opzoekt.
    const adressID = adress?.ID || ''

    // Het vaste adres van het huishouden (tot de migratie: de oude gegevens-
    // regels). De toevoeging telt mee: zonder kiest de controle in een pand met
    // 12A en 12B geen woning, in plaats van die van de buren. Een opgeslagen
    // 'undefined' is geen toevoeging (readHouseholdField filtert die).
    // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T5)
    // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
    const { postcode, huisnummer, toevoeging } = useMemo(() => adressLocationInput(adress, personaData), [adress, personaData])
    const inputKey = geocodeInputKey({ postcode, huisnummer, toevoeging })

    const [status, setStatus] = useState('idle')
    const [resolved, setResolved] = useState(null)
    // Wat de controle zei: 'match', 'mismatch' (PDOK vond een ánder adres),
    // 'ambiguous' (meerdere woningen, geen toevoeging), 'not-found' of
    // 'incomplete'. Bij mismatch staat in `found` welk adres het wél vond, bij
    // 'ambiguous' in `units` welke woningen er zijn, zodat het scherm dat kan
    // zeggen.
    const [controle, setControle] = useState(/** @type {import('../helpers/pdok/verifyAddressMatch').AddressCheck} */ ({ verdict: 'incomplete', found: null }))
    // De laatste opzoeking per invoer. Na onze eigen write komt de snapshot
    // terug en draait het effect opnieuw; dezelfde invoer hoeft dan niet nóg
    // een keer naar PDOK.
    /** @type {React.MutableRefObject<{key: string, check: import('../helpers/pdok/verifyAddressMatch').AddressCheck|null}>} */
    const lastCheck = useRef({ key: '', check: null })

    useEffect(() => {
        const input = { postcode, huisnummer, toevoeging }
        const plan = canReuseLocation(existing, input)
        if (plan.reuse) {
            setStatus('ok')
            // Ook hier: een huishouden waarvan het adres al opgezocht is heeft
            // net zo goed een deelname-rij nodig. Alleen bij een VERSE
            // geocodering aanmelden zou betekenen dat elk bestaand huishouden
            // er nooit een krijgt — gemeten toen de eerste rij uitbleef. Een
            // vroege return is óók een poort.
            // AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
            meldDeelnameAan(adressID)
            return
        }
        if (!adressDocid) {
            setStatus('idle')
            return
        }
        if (!postcode || !huisnummer) {
            setStatus('missing-input')
            return
        }

        const apply = (check) => {
            setControle(check)
            const write = buildLocationWrite(check, input)
            setResolved(write.status === 'failed' ? null : write.Location)
            setStatus(write.status)
            // Zelfde invoer als wat er al staat: wegschrijven verandert niets.
            if (!plan.write) return
            db.collection('Adresses').doc(adressDocid).update({
                Location: { ...write.Location, GeocodedAt: firebase.firestore.FieldValue.serverTimestamp() },
                // In DEZELFDE write als de Location waaruit hij is afgeleid, en
                // expliciet leeg zonder woning: een blijven staan sleutel wijst
                // naar een woning die we niet kunnen vaststellen.
                HouseholdKey: write.HouseholdKey,
            })
                // Nu de sleutel er is, kan het huishouden aan de programma's
                // van zijn organisatie gekoppeld worden. Server-side, want de
                // deelname-rij is cross-tenant en heeft een poort op Members[].
                // AI-CONTEXT: See docs/features/energiearmoede-arnhem-ketensamenwerking.md
                .then(() => { if (write.status === 'ok') meldDeelnameAan(adressID) })
                .catch(err => console.error('Failed to write Location to Adresses:', err))
        }

        if (lastCheck.current.key === inputKey && lastCheck.current.check) {
            apply(lastCheck.current.check)
            return
        }

        const controller = new AbortController()
        setStatus('resolving')

        // Niet blind docs[0]: op '7201AB 40' kan PDOK 7201AB 42 teruggeven, en
        // in een pand met 12A en 12B kiest de toevoeging. checkAddress zegt welke
        // woning het is, of waarom het er niet (eenduidig) bij zit.
        checkAddress({ ...input, signal: controller.signal })
            .then(check => {
                lastCheck.current = { key: inputKey, check }
                apply(check)
            })
            .catch(err => {
                if (err.name === 'AbortError') return
                console.error('PDOK geocode failed', err)
                setStatus('failed')
            })

        return () => controller.abort()
    }, [adressDocid, adressID, postcode, huisnummer, toevoeging, inputKey, existing?.Lat, existing?.Lng, existing?.Postcode, existing?.Huisnummer, existing?.HuisnummerToevoeging, existing?.GeocodedFrom, existing?.GeocodeStatus])

    const location = useMemo(
        () => (canReuseLocation(existing, { postcode, huisnummer, toevoeging }).reuse ? existing : resolved),
        [existing, resolved, postcode, huisnummer, toevoeging],
    )

    return { location, status, ...controle }
}

export default useEnsureAdressLocation
